/**
 * Filter entity class
 */

import moment from 'moment'

/**
 * FilterFactory
 * returns Filter entity class
 *
 * @class FilterFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Filter} class
 *
 * @ngdoc factory
 * @name mwApp.factory:FilterFactory
 */
/* @ngInject */
export default function FilterFactory (Entity, onOffMixin) {
  // use generic hasOwnProperty function
  let hasOwnProperty = Object.prototype.hasOwnProperty

  /* === Filter class === */

  /**
   * Filter model
   *
   * @class Filter
   * @classdesc Filter model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @param {Object} [object] - initial object, containing Filter entity fields
   * @return {models.Filter} Filter entity
   */
  function Filter () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Filter.prototype = Object.create(Entity.prototype)
  Filter.prototype.constructor = Filter

  /* === User instance methods & fields === */

  Object.assign(Filter.prototype, {

    /**
     * Instance fields by types
     *
     * @memberof models.Filter#
     * @type {Object}
     */
    _fieldsByTypes: {
      default: [
        'reportElementType'
      ],
      SUBSCRIBER_STATUS: [
        'status',
        'from',
        'to'
      ],
      SUBSCRIBER_AUTO_PAY: [
        'status'
      ],
      SUBSCRIBER_ENABLED: [
        'status'
      ],
      SUBSCRIBER_NEW_SUBSCRIBERS_DATE_FROM_TO: [
        'from',
        'to'
      ],
      SUBSCRIBER_NEW_SUBSCRIBERS: [
        'enabled'
      ],
      SUBSCRIBER_NEW_SUBSCRIBERS_DAYS: [
        'days'
      ],
      STREAM_DATE_FROM_TO: [
        'from',
        'to'
      ],
      STREAM_TYPE: [
        'streamType'
      ],
      STREAM_NETWORKS: [
        'networkIds'
      ],
      STREAM_CHANNELS: [
        'channelIds'
      ],
      STREAM_PLATFORMS: [
        'platformTypes'
      ],
      STREAM_REPORTING_METHOD: [
        'method'
      ],
      REPORT_DISPLAY_MODE: [
        'displayMode'
      ],
      REPORT_LAYOUT_CHART: [
        'topCount'
      ],
      REPORT_LAYOUT_TABLE_LIMIT: [
        'tableLimit'
      ],
      REPORT_TIME_UNIT: [
        'timeUnit'
      ],
      STREAM_SUBSCRIBERS: [
        'subscriberIds'
      ],
      STREAM_DEVICES: [
        'deviceIds'
      ],
      STREAM_SUBSCRIBER_FIELD: [
        'fieldName',
        'fieldValue'
      ],
      STREAM_DEVICE_FIELD: [
        'fieldName',
        'fieldValue'
      ]
    },

    /**
     * Condition for fields by types
     *
     * @memberof models.Filter#
     * @type {Object}
     */
    _fieldsByTypesCondition: {
      SUBSCRIBER_STATUS: function (self, ret) {
        if (self.$disableStatusRange) {
          ret.from = ret.to = ''
        }
      }
    },

    /**
     * Condition for fields by types
     *
     * @memberof models.Filter#
     * @type {Object}
     */
    _fieldsByTypesConditionPrepare: {
      STREAM_TYPE: function (self) {
        self.streamType = 'TV'
      }
    },

    /**
     * Instance fields
     *
     * @memberof models.Filter#
     * @type {Object}
     */
    _fields: {
      reportElementType: String,
      abstractFilterValues: [],
      abstractFilterValues2: [],
      from: moment,
      to: moment,
      $fromDate: Date,
      $toDate: Date,
      $fromDateTime: Date,
      $toDateTime: Date,
      status: String,
      $disableStatusRange: Boolean(true),
      columnOrder: Number,
      days: Number,
      entityColumnName: String,
      enabled: Boolean,
      method: String,
      streamType: String,
      networkIds: [],
      channelIds: [],
      platformTypes: [],
      subscriberIds: [],
      deviceIds: [],
      topCount: Number,
      displayMode: String,
      tableLimit: 10,
      timeUnit: String,
      fieldName: String,
      fieldValue: String
    },

    /**
     * Prepare object
     *
     * @memberof models.Filter#
     * @override
     */
    _prepare: function () {
      /* Note: set default values. */
      this.$fromDate = new Date()
      this.$toDate = moment().add(1, 'day').toDate()
      this.days = this.days || 1

      if (typeof this._fieldsByTypesConditionPrepare[this.reportElementType] === 'function') {
        this._fieldsByTypesConditionPrepare[this.reportElementType](this)
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.Filter#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      this.from = this.$fromDateTime
        ? moment.utc(this.$fromDateTime).tz(moment.defaultZone.name)
        : moment.utc(this.$fromDate).tz(moment.defaultZone.name).startOf('day')
      this.to = this.$toDateTime
        ? moment.utc(this.$toDateTime).tz(moment.defaultZone.name)
        : moment.utc(this.$toDate).tz(moment.defaultZone.name).endOf('day')

      let ret = Entity.prototype._object.apply(this, arguments)

      let fields = this._fieldsByTypes.default.concat(this._fieldsByTypes[ret.reportElementType])
      for (let f in ret) {
        if (hasOwnProperty.call(ret, f) && fields.indexOf(f) === -1) {
          delete ret[f]
        }
      }

      if (typeof this._fieldsByTypesCondition[ret.reportElementType] === 'function') {
        this._fieldsByTypesCondition[ret.reportElementType](this, ret)
      }

      return ret
    }
  }, onOffMixin)

  /* === User static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Filter, Entity)
  Filter._name = 'Filter'
  Filter.init()

  return Filter
}
